import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';

import {
  Modal,
  InlineTextInput,
  InlineSelect,
  BasicButton,
  SubmitButton,
} from '../../../components/Base';
import Collapsible from '../../../components/Collapsible';
import { fetchProvince, fetchCity, fetchDistrict } from '../../../actions/rajaongkir';
import { submitWarehouse, updateWarehouse } from '../../../actions/warehouse';

function WarehouseCreate(props, ref) {
  const { register, errors, handleSubmit, setValue } = useForm();

  const [isVisible, setIsVisible] = useState(false);
  const [initialWarehouse, setInitialWarehouse] = useState(null);
  const [provinceName, setProvinceName] = useState('');
  const [cityName, setCityName] = useState('');
  const [districtName, setDistrictName] = useState('');
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const { provinces, cities, districts } = useSelector((state) => state.rajaongkir);
  const dispatch = useDispatch();

  useImperativeHandle(ref, () => ({
    showForm,
    showFormWithInitialData,
  }));

  useEffect(() => {
    dispatch(fetchProvince());
  }, []);

  useEffect(() => {
    if (initialWarehouse) {
      setTimeout(() => {
        setValue('name', initialWarehouse.name);
        setValue('street_address', initialWarehouse.street_address);
        setValue('province_id', `${initialWarehouse.province_id}`);
        setProvinceName(initialWarehouse.province);
        setValue('zip_code', initialWarehouse.zip_code);
        setValue('pic', initialWarehouse.pic);
        setValue('pic_phone', initialWarehouse.pic_phone);

        dispatch(fetchCity(initialWarehouse.province_id))
          .then(() => dispatch(fetchDistrict(initialWarehouse.city_id)))
          .then(() => {
            setValue('city_id', `${initialWarehouse.city_id}`);
            setCityName(initialWarehouse.city);
            setValue('district_id', `${initialWarehouse.district_id}`);
            setDistrictName(initialWarehouse.district);
          });
      }, 100);
    }
  }, [initialWarehouse]);

  const showForm = () => setIsVisible(true);
  const closeForm = () => {
    setIsVisible(false);
    setInitialWarehouse(null);
    setProvinceName('');
    setCityName('');
    setDistrictName('');
    setIsSubmittingForm(false);
  };

  const showFormWithInitialData = (data) => {
    setInitialWarehouse(data);
    showForm();
  };

  const onChangeProvince = (e) => {
    const selectedProvince = provinces.find((p) => p.id === Number(e.target.value));
    setProvinceName(selectedProvince.name);
    dispatch(fetchCity(selectedProvince.id));
  };

  const onChangeCity = (e) => {
    const selectedCity = cities.find((c) => c.id === Number(e.target.value));
    setCityName(selectedCity.name);
    dispatch(fetchDistrict(selectedCity.id));
  };

  const onChangeDistrict = (e) => {
    const selectedDistrict = districts.find((d) => d.id === Number(e.target.value));
    setDistrictName(selectedDistrict.name);
  };

  const onSubmitForm = (data) => {
    setIsSubmittingForm(true);

    data.province = provinceName;
    data.city = cityName;
    data.district = districtName;
    // data.is_default = 1;

    if (initialWarehouse) {
      dispatch(updateWarehouse(initialWarehouse.id, data))
        .then(() => closeForm())
        .catch(() => setIsSubmittingForm(false));
    } else {
      dispatch(submitWarehouse(data))
        .then(() => closeForm())
        .catch(() => setIsSubmittingForm(false));
    }
  };

  return (
    <Modal
      isVisible={isVisible}
      size="large"
      ModalContent={
        <div className="sm:flex sm:items-start">
          <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
            <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
              {initialWarehouse ? 'Ubah Store' : 'Tambah Store Baru'}
            </Dialog.Title>
            <div className="mt-5">
              <form id="createForm" onSubmit={handleSubmit(onSubmitForm)}>
                <InlineTextInput
                  type="text"
                  title="Nama Store"
                  inputRef={register({ required: true })}
                  name="name"
                  errorMessage={errors.name && 'Kolom Nama Store harus diisi'}
                />

                <Collapsible title="Alamat">
                  <InlineTextInput
                    type="text"
                    title="Alamat"
                    placeholder="Nama Jalan, Blok Ruman, No. Apartemen, dll."
                    inputRef={register({ required: true })}
                    name="street_address"
                    errorMessage={errors.street_address && 'Kolom Alamat harus diisi'}
                  />

                  <InlineSelect
                    title="Provinsi"
                    inputRef={register({ required: true })}
                    name="province_id"
                    options={provinces.map((province) => (
                      <option key={province.id} value={province.id}>
                        {province.name}
                      </option>
                    ))}
                    errorMessage={errors.province_id && 'Kolom Province harus dipilih'}
                    onChange={onChangeProvince}
                  />

                  <InlineSelect
                    title="Kota"
                    inputRef={register({ required: true })}
                    name="city_id"
                    options={cities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))}
                    errorMessage={errors.city_id && 'Kolom Kota harus dipilih'}
                    onChange={onChangeCity}
                  />

                  <InlineSelect
                    title="Kecamatan"
                    inputRef={register({ required: true })}
                    name="district_id"
                    options={districts.map((district) => (
                      <option key={district.id} value={district.id}>
                        {district.name}
                      </option>
                    ))}
                    errorMessage={errors.district_id && 'Kolom Kecamatan harus dipilih'}
                    onChange={onChangeDistrict}
                  />

                  <InlineTextInput
                    type="number"
                    title="Zip Code"
                    inputRef={register({ required: true })}
                    name="zip_code"
                    errorMessage={errors.zip_code && 'Kolom Zip Code harus diisi'}
                    maxLength={5}
                  />
                </Collapsible>

                <Collapsible title="PIC">
                  <InlineTextInput
                    type="text"
                    title="Nama PIC"
                    inputRef={register()}
                    name="pic"
                    errorMessage={errors.pic && 'Kolom Nama PIC harus diisi'}
                  />

                  <InlineTextInput
                    type="text"
                    title="PIC's Phone"
                    inputRef={register()}
                    name="pic_phone"
                    errorMessage={errors.pic_phone && 'Kolom PIC Phone harus diisi'}
                  />
                </Collapsible>

                <Collapsible title="iPay88 Credentials">
                  <InlineTextInput
                    type="text"
                    title="Merchant Code"
                    inputRef={register()}
                    name="ipay88_merchant_code"
                    defaultValue={initialWarehouse?.ipay88_merchant_code}
                  />

                  <InlineTextInput
                    type="text"
                    title="Merchant Key"
                    inputRef={register()}
                    name="ipay88_merchant_key"
                    defaultValue={initialWarehouse?.ipay88_merchant_key}
                  />
                </Collapsible>
              </form>
            </div>
          </div>
        </div>
      }
      ModalButton={
        <>
          <SubmitButton
            type="submit"
            form="createForm"
            text="Submit"
            textClass="text-white text-xs"
            isLoading={isSubmittingForm}
          />
          <BasicButton text="Cancel" textClass="text-white text-xs mr-2" onClick={closeForm} />
        </>
      }
    />
  );
}

export default forwardRef(WarehouseCreate);
